// frontend/src/app/components/projects/project-corpus/dialogs/edit-metadata-dialog.component.ts
import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { BaseAlertComponent } from '../../../alerts/base-alert/base-alert.component';
import { BaseModalComponent } from '../../../modals/base-modal/base-modal.component';
import { ProjectCorpusService } from '../../../../project-corpus/domain/project-corpus.service';
import { PaperMetadataPatch, ProjectPaper } from '../../../../project-corpus/domain/types';
import {
  WORK_TYPE_ALL,
  SOURCE_TYPE_ALL,
  TypeOption,
} from '../../../../project-corpus/domain/openalex-types';

const DOI_REGEX = /10\.\d{4,9}\/.+/;

/** Strip uppercase-prefix from external IDs like "BIBTEX:citationKey". */
function stripExternalIdPrefix(id: string): string {
  const m = id.match(/^([A-Z][A-Z_]+):(.*)/);
  return m ? m[2] : id;
}

/**
 * Edit-paper-metadata dialog. Rendered (via `BaseModalComponent`) only when
 * an input `paper !== null`.
 *
 * Form fields:
 *  - title (required, trimmed)
 *  - authors (comma-separated input, persisted as `string[]`)
 *  - year (number; 1000 ≤ year ≤ currentYear+1)
 *  - DOI (regex `^10\.\d{4,9}/.+$` — warn but allow save if mismatch)
 *  - abstract (textarea)
 *  - venue (text)
 *
 * If `paper.projectsLinkedCount > 1`, a notice above the form explains that
 * edits propagate to the other projects.
 *
 * Save calls `service.editPaper(paper.id, patch)`. On success the dialog
 * closes; on error a `BaseAlertComponent` is shown inline and the dialog
 * stays open.
 *
 * ESC and backdrop closing are handled by `BaseModalComponent`.
 */
@Component({
  selector: 'app-edit-metadata-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, BaseAlertComponent, BaseModalComponent],
  templateUrl: './edit-metadata-dialog.component.html',
  styleUrls: ['./edit-metadata-dialog.component.scss'],
})
export class EditMetadataDialogComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly service = inject(ProjectCorpusService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _paper = signal<ProjectPaper | null>(null);
  private readonly _readonly = signal(false);
  /** Mirrors the reactive form's value as a signal so computeds can react. */
  private readonly _formValue = signal<{
    title: string;
    authorsCsv: string;
    year: number | null;
    doi: string;
    abstractText: string;
    venue: string;
    workType: string;
    sourceType: string;
  }>({
    title: '',
    authorsCsv: '',
    year: null,
    doi: '',
    abstractText: '',
    venue: '',
    workType: '',
    sourceType: '',
  });
  readonly submitting = signal(false);
  readonly submitError = signal<string | null>(null);

  readonly form = this.fb.nonNullable.group({
    title: ['', [Validators.required]],
    authorsCsv: [''],
    year: this.fb.control<number | null>(null),
    doi: [''],
    abstractText: [''],
    venue: [''],
    workType: [''],
    sourceType: [''],
  });

  get workTypeOptions(): readonly TypeOption[] {
    const paper = this._paper();
    if (paper?.workType && !WORK_TYPE_ALL.some((o) => o.value === paper.workType)) {
      return [...WORK_TYPE_ALL, { value: paper.workType, label: paper.workType }];
    }
    return WORK_TYPE_ALL;
  }

  get sourceTypeOptions(): readonly TypeOption[] {
    const paper = this._paper();
    if (paper?.sourceType && !SOURCE_TYPE_ALL.some((o) => o.value === paper.sourceType)) {
      return [...SOURCE_TYPE_ALL, { value: paper.sourceType, label: paper.sourceType }];
    }
    return SOURCE_TYPE_ALL;
  }

  @Input()
  set paper(value: ProjectPaper | null) {
    this._paper.set(value);
    if (value) {
      this.form.reset({
        title: value.title,
        authorsCsv: value.authors.join(', '),
        year: value.year,
        doi: value.doi ?? '',
        abstractText: value.abstractText ?? '',
        venue: value.venue ?? '',
        workType: value.workType ?? '',
        sourceType: value.sourceType ?? '',
      });
      // `form.reset` emits valueChanges asynchronously; mirror immediately
      // so computed warnings see the seeded values in the same tick.
      this._formValue.set(this.snapshotFormValue());
      this.submitError.set(null);
    }
  }
  get paper(): ProjectPaper | null {
    return this._paper();
  }

  @Input()
  set readonly(value: boolean) {
    this._readonly.set(value);
    if (value) {
      this.form.disable();
    } else {
      this.form.enable();
    }
  }
  get readonly(): boolean {
    return this._readonly();
  }

  @Output() openChange = new EventEmitter<boolean>();

  readonly currentPaper = this._paper.asReadonly();
  readonly isOpen = computed(() => this._paper() !== null);
  readonly currentYear = new Date().getFullYear();
  readonly maxYear = this.currentYear + 1;

  readonly otherProjectsCount = computed(() => {
    const p = this._paper();
    return p ? Math.max(0, p.projectsLinkedCount - 1) : 0;
  });

  /**
   * True when the DOI input is non-empty AND doesn't match the OPSI regex.
   * Depends on the mirrored `_formValue` signal so it re-evaluates as the
   * user types (reactive form controls aren't signals).
   */
  readonly doiWarning = computed(() => {
    const v = this._formValue().doi?.trim() ?? '';
    if (v.length === 0) return false;
    return !DOI_REGEX.test(v);
  });

  readonly doiUrl = computed(() => {
    const v = this._formValue().doi?.trim() ?? '';
    if (!v) return null;
    // Already a valid URL
    if (v.startsWith('http://') || v.startsWith('https://')) return v;
    // Bare DOI
    if (DOI_REGEX.test(v)) return `https://doi.org/${v}`;
    return null;
  });

  readonly yearWarning = computed(() => {
    const v = this._formValue().year;
    if (v === null || v === undefined) return null;
    if (v < 1000 || v > this.maxYear) {
      return `Year must be between 1000 and ${this.maxYear}.`;
    }
    return null;
  });

  readonly externalIdDisplay = computed(() => {
    const p = this._paper();
    if (!p?.externalId) return null;
    return stripExternalIdPrefix(p.externalId);
  });

  readonly externalIdIsUrl = computed(() => {
    if (
      this.externalIdDisplay()?.startsWith('http://') ||
      this.externalIdDisplay()?.startsWith('https://')
    )
      return this.externalIdDisplay();
    return null;
  });

  ngOnInit(): void {
    // Mirror form value into `_formValue` so the `doiWarning` / `yearWarning`
    // computeds re-evaluate as the user types. Without this bridge, the
    // computeds would never see updates (reactive form `.value` isn't a
    // signal and so isn't tracked by `computed`).
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this._formValue.set(this.snapshotFormValue());
    });
  }

  private snapshotFormValue(): {
    title: string;
    authorsCsv: string;
    year: number | null;
    doi: string;
    abstractText: string;
    venue: string;
    workType: string;
    sourceType: string;
  } {
    const v = this.form.getRawValue();
    return {
      title: v.title ?? '',
      authorsCsv: v.authorsCsv ?? '',
      year: v.year ?? null,
      doi: v.doi ?? '',
      abstractText: v.abstractText ?? '',
      venue: v.venue ?? '',
      workType: v.workType ?? '',
      sourceType: v.sourceType ?? '',
    };
  }

  close(): void {
    this._paper.set(null);
    this.openChange.emit(false);
  }

  onOpenChange(open: boolean): void {
    if (!open) {
      this.close();
    }
  }

  save(): void {
    if (this.submitting()) return;
    const paper = this._paper();
    if (!paper) return;

    const titleRaw = this.form.controls.title.value?.trim() ?? '';
    if (titleRaw.length === 0) {
      this.form.controls.title.markAsTouched();
      return;
    }

    if (this.yearWarning()) {
      // Year is invalid — refuse to save.
      this.form.controls.year.markAsTouched();
      return;
    }

    const authorsCsv = this.form.controls.authorsCsv.value ?? '';
    const authors = authorsCsv
      .split(',')
      .map((a) => a.trim())
      .filter((a) => a.length > 0);
    const yearVal = this.form.controls.year.value;
    const doiVal = (this.form.controls.doi.value ?? '').trim();
    const abstractVal = (this.form.controls.abstractText.value ?? '').trim();
    const venueVal = (this.form.controls.venue.value ?? '').trim();
    const workTypeVal = (this.form.controls.workType.value ?? '').trim();
    const sourceTypeVal = (this.form.controls.sourceType.value ?? '').trim();

    const patch: PaperMetadataPatch = {
      title: titleRaw,
      authors,
      year: yearVal === null || yearVal === undefined ? null : yearVal,
      doi: doiVal.length === 0 ? null : doiVal,
      abstractText: abstractVal.length === 0 ? null : abstractVal,
      venue: venueVal.length === 0 ? null : venueVal,
      workType: workTypeVal.length === 0 ? null : workTypeVal,
      sourceType: sourceTypeVal.length === 0 ? null : sourceTypeVal,
    };

    this.submitting.set(true);
    this.submitError.set(null);
    this.service.editPaper(paper.id, patch).subscribe({
      next: () => {
        this.submitting.set(false);
        this.close();
      },
      error: (err: unknown) => {
        this.submitError.set(this.toMessage(err));
        this.submitting.set(false);
      },
    });
  }

  private toMessage(err: unknown): string {
    if (err instanceof Error) return err.message;
    return String(err);
  }
}
